import { useState } from 'react'
import { api, type Account } from '../api'
import { until } from '../format'
import { confirmAction, haptic } from '../telegram'

/**
 * What each server state means to the user. An unknown name (a state added on the server before the
 * panel learned it) falls back to a neutral entry instead of crashing the whole screen on
 * `undefined.label`.
 */
const SESSION_TEXT: Record<string, { label: string; tone: string }> = {
  Valid: { label: 'Аккаунт подключён', tone: 'ok' },
  ExpiringSoon: { label: 'Аккаунт подключён — игровой пропуск скоро обновится', tone: 'ok' },
  Expired: { label: 'Аккаунт подключён — игровой пропуск обновится при следующем запуске', tone: 'ok' },
  Missing: { label: 'Аккаунт подключён — игровой пропуск будет получен при первом запуске', tone: 'ok' },
  NeedsCredential: { label: 'Аккаунт не подключён', tone: 'bad' },
}
const UNKNOWN_SESSION = { label: 'Состояние неизвестно', tone: 'warn' }

/**
 * The connected account: whether the bot can enter the game for it, and the way to take that access
 * away. Shown once the account is connected; before that the connect card stands in its place.
 */
export function AccountCard({ account, onChanged }: { account: Account; onChanged: () => Promise<unknown> }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const state = account.gameSession
  // With a renewable token, "expiring" and "expired" are the bot's problem, not the user's: it mints
  // a new one on the next run. Without one, they are exactly what the user needs to hear.
  const session =
    (state.state === 'ExpiringSoon' || state.state === 'Expired') && !state.renewable
      ? { label: state.state === 'Expired' ? 'Доступ истёк — подключите аккаунт заново' : 'Доступ скоро истечёт', tone: 'bad' }
      : (SESSION_TEXT[state.state] ?? UNKNOWN_SESSION)
  const sessionLeft = until(account.gameSession.expiresAtUtc)

  async function disconnect() {
    const ok = await confirmAction(
      'Отключить аккаунт? Мы удалим ключ сессии Telegram и игровой пропуск, все функции выключатся. Подключить снова можно в любой момент.',
    )
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      await api.disconnect()
      haptic('ok')
      await onChanged()
    } catch {
      haptic('error')
      setError('Не получилось отключить — попробуйте ещё раз.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card">
      <div className="row">
        <span className={`dot ${session.tone}`} />
        <div className="grow">
          <div className="title">{session.label}</div>
          {sessionLeft && (state.state === 'Valid' || state.state === 'ExpiringSoon') && (
            <div className="muted">Пропуск действует ещё {sessionLeft}</div>
          )}
        </div>
      </div>

      {error && <p className="banner bad">{error}</p>}

      <button className="link small" onClick={() => void disconnect()} disabled={busy}>
        {busy ? 'Отключаем…' : 'Отключить аккаунт'}
      </button>
    </section>
  )
}
